import "zod-openapi/extend";

import {
  APINewField,
  APIUpdateField,
  type APIFieldType,
} from "server/services/tables/types/fields";
import { z } from "zod";
import { APIFieldLatest } from "../services/tables/types/tables-latest";
import { toOutputSchema } from "./util";
import { TableIdSchema } from "@/server/schemas/table-tables";

/**
 * Taken from https://gitlab.com/zapier/team-fpv/tablesui/-/blob/main/packages/tablesui/src/field-values/constants.ts.
 * Use case: When LLM talks about field types, it should refer to them as the user sees them in the UI, not how they are stored in the database.
 */
export const TABLE_FIELD_TYPE_KEY_TO_HUMAN_READABLE_NAME = {
  ai_formula: "AI Field",
  boolean: "Checkbox",
  button_continue_zap: "Button",
  button_trigger_zap: "Button",
  currency: "Currency",
  datetime: "Date & Time",
  decimal: "Number",
  email: "Email",
  formula: "Formula",
  json: "JSON",
  labeled_string: "Dropdown",
  link: "Link",
  multiple_link: "Link",
  linked_record: "Linked Record",
  multiple_boolean: "Checkbox",
  multiple_datetime: "Date & Time",
  multiple_decimal: "Number",
  multiple_email: "Email",
  multiple_json: "JSON",
  multiple_labeled_string: "Dropdown",
  multiple_linked_record: "Linked Record",
  multiple_number: "Number",
  multiple_string: "Dropdown",
  multiple_text: "Long text",
  multiple_uuid: "UUID",
  number: "Number",
  phone_number: "Phone number",
  string: "Text",
  text: "Long text",
  uuid: "UUID",
} as const satisfies Record<APIFieldType, string>;

type TableFieldHumanReadableName =
  (typeof TABLE_FIELD_TYPE_KEY_TO_HUMAN_READABLE_NAME)[keyof typeof TABLE_FIELD_TYPE_KEY_TO_HUMAN_READABLE_NAME];

const tableFieldsHumanReadableNames = Object.values(
  TABLE_FIELD_TYPE_KEY_TO_HUMAN_READABLE_NAME
) as [TableFieldHumanReadableName, ...TableFieldHumanReadableName[]];

export const TableFieldHumanReadableTypeSchema = z
  .enum(tableFieldsHumanReadableNames)
  .describe("The human-readable translation of the 'type' field property.");

const tableFieldTypeSchema = z
  .enum([
    "string",
    "multiple_string",
    "labeled_string",
    "multiple_labeled_string",
    "text",
    "multiple_text",
    "boolean",
    "multiple_boolean",
    "number",
    "multiple_number",
    "decimal",
    "multiple_decimal",
    "datetime",
    "multiple_datetime",
    "uuid",
    "multiple_uuid",
    "json",
    "multiple_json",
    "formula",
    "button_trigger_zap",
    "button_continue_zap",
    "email",
    "multiple_email",
    "link",
    "multiple_link",
    "ai_formula",
    "linked_record",
    "multiple_linked_record",
    "phone_number",
    "currency",
    "multiple_link",
  ])
  .describe("The type of the field.");

export const tableFieldSchema = z.object({
  id: z.number(),
  type: tableFieldTypeSchema,
  name: z.string(),
  options: z.record(z.any()).optional(),
  created_at: z.string().optional(),
});

export const listFieldsSchema = z.object({
  tableId: TableIdSchema,
});

export const getFieldChoicesSchema = z.object({
  tableId: TableIdSchema,
  fieldId: z.number(),
});

export const newFieldSchema: z.ZodType<APINewField> = z.object({
  type: tableFieldTypeSchema,
  name: z.string(),
  options: z.record(z.any()).optional(),
  created_at: z.string().optional(),
  edited_at: z.string().optional(),
});

export type newFieldType = z.infer<typeof newFieldSchema>;

export const createFieldsSchema = z.object({
  tableId: TableIdSchema,
  body: z.object({ new_fields: newFieldSchema.array() }),
});

const updateFieldSchema: z.ZodType<APIUpdateField> = z.object({
  id: z.number().int(),
  type: z.enum([
    "string",
    "multiple_string",
    "labeled_string",
    "multiple_labeled_string",
    "text",
    "multiple_text",
    "boolean",
    "multiple_boolean",
    "number",
    "multiple_number",
    "decimal",
    "multiple_decimal",
    "datetime",
    "multiple_datetime",
    "uuid",
    "multiple_uuid",
    "json",
    "multiple_json",
    "formula",
    "button_trigger_zap",
    "button_continue_zap",
    "email",
    "multiple_email",
    "link",
    "phone_number",
    "currency",
    "ai_formula",
    "multiple_link",
  ]),
  name: z.string(),
  options: z.record(z.any()).optional(),
});

export const tablesFieldConversionStrategy = z.enum([
  "convert_if_safe",
  "force_conversion",
]);
export type TablesFieldConversionStrategy = z.infer<
  typeof tablesFieldConversionStrategy
>;

export const updateFieldsSchema = z.object({
  tableId: TableIdSchema,
  body: z.object({
    updated_fields: updateFieldSchema.array(),
    change_options: z.object({
      conversion_strategy: tablesFieldConversionStrategy.optional(),
    }),
  }),
});

export const interactWithFieldSchema = z.object({
  tableId: TableIdSchema,
  recordId: z.string(),
  fieldId: z.number().int(),
  body: z.object({
    interaction: z.object({
      kind: z.literal("button_click"),
      button_click_label: z.string().optional(),
      triggered_by: z.string().optional(),
      is_enabled: z.boolean().optional(),
      custom_label: z.string().optional(),
      disable_on_click: z.boolean().optional(),
    }),
    change_options: z.record(z.unknown()).optional(),
  }),
});

export const getFieldConversionsSchema = z.object({
  tableId: TableIdSchema,
  fieldId: z.number().int(),
});

export const TableFieldSchema = z
  .object({
    id: z.number(),
    created_at: z.string().optional(),
    edited_at: z.string().optional(),
    type: tableFieldTypeSchema,
    name: z.string(),
    options: z.record(z.any()).optional(),
    config: z.record(z.any()).optional(),
    is_order_field: z.boolean().optional(),
    is_filter_field: z.boolean().optional(),
    is_selected_field: z.boolean().optional(),
  })
  .openapi({ ref: "TableField" }) satisfies z.ZodType<APIFieldLatest>;

export const TableFieldsResponseSchema = toOutputSchema(
  z.object({
    results: z.array(TableFieldSchema),
  })
);

export type TableFieldResponse = z.infer<typeof TableFieldsResponseSchema>;
